'use client';

import React, { useMemo } from 'react';
import { IRegionShort } from '@/api/region/types';
import { IResortPathInfo } from '@/api/resort/types';
import { NextLink } from '@/components/blocks/NextLink';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { AdUnitContext } from '@/tracking/ads/components/AdUnitContext';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Footer } from '@/components/blocks/Footer';
import { Routes } from '@/routes';

export type SitemapProps = {
  resorts: Array<IResortPathInfo>,
  regions: Array<IRegionShort>,
};

export const DEBUG_TAG = 'sitemap-page';
export const TEMPLATE_ID = '9.10';
export const TEMPLATE_NAME = 'Sitemap';

const NO_REGION = 'NO_REGION';

const listClass = 'grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-3';

export default function SiteMapPage({ resorts, regions }: SitemapProps) {
  const { t } = useOTSTranslation('common');

  const resortsInRegions = useMemo(() => {
    const allRegions = new Set(regions.map((r) => String(r.uuid)));
    const data: Record<string, Array<IResortPathInfo>> = {};
    resorts.forEach((r) => {
      const bucket = allRegions.has(r.region_id) ? r.region_id : NO_REGION;
      data[bucket] = data[bucket] || [];
      data[bucket].push(r);
    });
    return data;
  }, [resorts, regions]);

  const sortedRegions = [...regions].sort((a, b) => a.title.localeCompare(b.title));
  const regionsWithResorts = sortedRegions.filter((r) => (resortsInRegions[r.uuid] || []).length);
  const emptyRegions = sortedRegions.filter((r) => !(resortsInRegions[r.uuid] || []).length);
  const orphanResorts = (resortsInRegions[NO_REGION] || []).sort((a, b) => a.title.localeCompare(b.title));

  return (
    <AdUnitContext adType="other" adTKey="unsubscibe">
      <HeaderNav current={HeaderNavCurLink.MAIN} />
      <main className="mx-auto max-w-5xl px-4 py-8 md:py-12">
        <h1 className="mb-10 font-heading text-3xl text-secondary md:text-4xl">
          {t('common.sitemapHeader')}
        </h1>

        <div className="space-y-10">
          {regionsWithResorts.map((region) => (
            <section key={region.uuid}>
              <h2 className="mb-3 font-heading text-xl text-secondary">
                <NextLink href={Routes.regionSnowReport(region)}>{region.title}</NextLink>
              </h2>
              <ul className={listClass}>
                {(resortsInRegions[region.uuid] || [])
                  .sort((a, b) => a.title.localeCompare(b.title))
                  .map((resort) => (
                    <li key={resort.uuid}>
                      <NextLink href={Routes.resortSnowReport(resort)}>{resort.title}</NextLink>
                    </li>
                  ))}
              </ul>
            </section>
          ))}

          {emptyRegions.length > 0 && (
            <section>
              <h2 className="mb-3 font-heading text-xl text-secondary">
                {t('common.sitemapAdditionalRegions')}
              </h2>
              <ul className={listClass}>
                {emptyRegions.map((region) => (
                  <li key={region.uuid}>
                    <NextLink href={Routes.regionSnowReport(region)}>{region.title}</NextLink>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {orphanResorts.length > 0 && (
            <section>
              <ul className={listClass}>
                {orphanResorts.map((resort) => (
                  <li key={resort.uuid}>
                    <NextLink href={Routes.resortSnowReport(resort)}>{resort.title}</NextLink>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </AdUnitContext>
  );
}
